/**
 * Sobreposições: gaveta lateral e modal.
 *
 * Ambas são diálogos de verdade (`role="dialog"`, `aria-modal`), fecham no
 * Esc e devolvem o foco a quem as abriu. Ficha e formulário são leitura densa:
 * se o teclado se perde dentro deles, a tela deixa de ser operável sem mouse.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { destravarRolagem, ehODoTopo, travarRolagem } from "./rolagem.js";

/**
 * A pilha de diálogos e a trava de rolagem vivem em `rolagem.ts`.
 *
 * Elas saíram daqui porque o bug que tinham era de ORDEM — dois diálogos
 * empilhados deixavam a página inteira sem rolagem até o F5 —, e ordem se
 * testa com chamadas de função, não montando componente. O arquivo de lá
 * conta a história completa.
 */
function useDialogo(aoFechar: () => void): React.RefObject<HTMLDivElement> {
  const caixa = useRef<HTMLDivElement>(null);

  // O fechar chega como função nova a cada render. Se ele fosse dependência do
  // efeito, o efeito rodaria de novo a cada tecla digitada e o `focus()` de
  // abertura roubaria o cursor do campo em que se está escrevendo. A ref deixa
  // o efeito rodar uma vez só, na montagem, sem congelar a versão do callback.
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;

  useEffect(() => {
    // Travar o fundo evita o efeito de "duas rolagens" — a de trás rolando
    // enquanto se lê a ficha é desorientador e faz perder a linha.
    const eu = travarRolagem();

    const anterior = document.activeElement as HTMLElement | null;
    caixa.current?.focus();

    const teclou = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      // Só o diálogo do topo responde: `stopPropagation` não impede outro
      // ouvinte no mesmo documento, então um Esc fecharia os dois de uma vez.
      if (!ehODoTopo(eu)) return;
      e.stopPropagation();
      fechar.current();
    };
    document.addEventListener("keydown", teclou);

    return () => {
      document.removeEventListener("keydown", teclou);
      destravarRolagem(eu);
      anterior?.focus?.();
    };
  }, []);

  return caixa;
}

export function Gaveta({
  rotulo,
  cabecalho,
  rodape,
  aoFechar,
  children,
}: {
  rotulo: string;
  cabecalho: ReactNode;
  rodape?: ReactNode;
  aoFechar: () => void;
  children: ReactNode;
}): JSX.Element {
  const caixa = useDialogo(aoFechar);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Fechar a ficha"
        onClick={aoFechar}
        className="absolute inset-0 bg-tinta/25 cursor-default"
      />
      <div
        ref={caixa}
        role="dialog"
        aria-modal="true"
        aria-label={rotulo}
        tabIndex={-1}
        className="relative h-full w-full max-w-[620px] bg-superficie border-l border-linha flex flex-col shadow-xl outline-none"
      >
        <div className="px-5 py-4 border-b border-linha flex items-start gap-3 shrink-0">
          <div className="min-w-0 flex-1">{cabecalho}</div>
          <button
            type="button"
            onClick={aoFechar}
            className="text-[12.5px] text-tinta-2 hover:text-tinta rounded-md px-2 py-1 shrink-0"
          >
            Fechar
          </button>
        </div>

        {/*
          `min-h-0` NÃO é enfeite, é o que faz esta área rolar.

          Item de flex tem `min-height: auto` por padrão, o que o impede de
          encolher abaixo do próprio conteúdo. Sem isso, `flex-1` cresce até o
          tamanho do conteúdo, `overflow-y-auto` nunca tem o que cortar, e a
          altura estourada é aparada pelo `h-full` do painel: o resto do
          formulário fica fora da tela, sem barra e sem responder à roda do
          mouse. E o fundo está travado, então nada rola em lugar nenhum.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-5 min-w-0">
          {children}
        </div>

        {rodape && (
          <div className="px-5 py-3.5 border-t border-linha bg-superficie-2 shrink-0">
            {rodape}
          </div>
        )}
      </div>
    </div>
  );
}

export function Modal({
  titulo,
  dica,
  largura = 640,
  rodape,
  aoFechar,
  children,
}: {
  titulo: string;
  dica?: string;
  largura?: number;
  rodape?: ReactNode;
  aoFechar: () => void;
  children: ReactNode;
}): JSX.Element {
  const caixa = useDialogo(aoFechar);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <button
        type="button"
        aria-label="Fechar"
        onClick={aoFechar}
        className="absolute inset-0 bg-tinta/25 cursor-default"
      />
      <div
        ref={caixa}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        className="relative w-full max-h-full bg-superficie border border-linha rounded-[10px] flex flex-col shadow-xl outline-none"
        style={{ maxWidth: largura }}
      >
        <div className="px-5 py-3.5 border-b border-linha flex items-center gap-3 shrink-0">
          <h2 className="text-[13px] font-semibold tracking-tight flex-1 min-w-0">
            {titulo}
          </h2>
          <button
            type="button"
            onClick={aoFechar}
            className="text-[12.5px] text-tinta-2 hover:text-tinta rounded-md px-2 py-1"
          >
            Fechar
          </button>
        </div>
        {dica && (
          <p className="px-5 pt-3 text-[12px] text-tinta-3 leading-relaxed">
            {dica}
          </p>
        )}
        {/* `min-h-0` pelo mesmo motivo da gaveta — ver a nota acima. Aqui o
            corte vem do `max-h-full`, e o formulário de "Novo cliente" é
            justamente um dos que passam da altura da tela num notebook. */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-4 min-w-0">
          {children}
        </div>
        {rodape && (
          <div className="px-5 py-3.5 border-t border-linha bg-superficie-2 flex items-center justify-end gap-2 flex-wrap shrink-0">
            {rodape}
          </div>
        )}
      </div>
    </div>
  );
}
